import { get } from 'lodash'
import { runInAction } from 'mobx'
import rootStore from '../..'
import { wrapWithLoading } from '../../../config/general-actions'
import { responseHasError } from '../../users/actions'
import { PLAN_FOR_DELETION_NR_OF_DAYS, warnPt } from '../../../config'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { requireDocumentationById } from '../../documentation/action'
import { deleteLstItem } from '../../../api/utils/api-delete'
import dayjs from 'dayjs'
import { translate as t } from '../../../cls/TranslationContext'
const _storeKey = 'uiLstDocumentation'

/**
 * @async
 * @function actLoadDocumentationList
 * @description Asynchronously loads list of documentations from the server and updates the UI list store.
 * @returns {Promise<void>}
 */
export const actLoadDocumentationList = wrapWithLoading(
	async () => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const params = {
			pg: rootStore[_storeKey].pg,
			rpg: rootStore[_storeKey].rpg,
			del: rootStore[_storeKey].del,
			search: rootStore[_storeKey].search,
			appType: rootStore[_storeKey].filter?.appType || ''
		}

		// TODO: remove hardcoded data object when API is ready for this module
		// const _response = await getDocumentationList(params)
		// const { data } = _response
		const data = {
			status: 'success',
			itms: [
				{
					documentation_id: '2753599c-6dba-40f5-983e-87d2e81bc8a9',
					name: t('__overview%_project'),
					description: t('single.document.overview.info'),
					body: '#',
					created_by: 'be5590fa-a0bd-4012-8680-f43cbf4c71bb',
					created_at: '2024-10-16T12:00:00Z',
					to_delete: false
				}
			],
			total: 1
		}

		const hasError = responseHasError(data)
		if (!hasError && data.status === 'success') {
			runInAction(() => {
				_actSync(get(data, ['itms'], []), get(data, ['total'], 0))
			})
		} else {
			//throw _response
			console.log('Response has error')
		}
	},
	{ loadingMessage: 'Loading Documentation' }
)

/**
 * @function _actSync
 * @description Syncs the server items with the store items.
 * @param {Array} srvItms - Server items.
 * @param {number} nr - Number of items.
 */
const _actSync = (srvItms, nr) => {
	warnPt(srvItms, nr)
	runInAction(() => {
		for (const _srvItm of srvItms) {
			const _storeItm = requireDocumentationById(_srvItm.documentation_id)
			_storeItm.updateFromServer(_srvItm)
		}
		rootStore[_storeKey].setIds(srvItms.map((it) => it.documentation_id))
		rootStore[_storeKey].setNr(nr)
	})
}

/**
 * @function actUndoOrDeleteDocumentation
 * @async
 * @description
 * Handles deletion or undo-deletion of a documentation item. If `status` is `true`, marks the
 * item for deletion by setting a future timestamp (based on `PLAN_FOR_DELETION_NR_OF_DAYS`). If `false`,
 * it removes the deletion marker.
 *
 * In demo mode, it simulates the operation without calling the API.
 * If not in demo mode, it makes an API call using `deleteLstItem`, then updates local MobX state.
 *
 * @param {string} documentationId - The unique identifier of the documentation item.
 * @param {boolean} [status=true] - If `true`, mark for deletion; if `false`, undo deletion.
 * @param {boolean} [demo=false] - If `true`, simulate deletion locally without server call.
 *
 * @returns {Promise<boolean|void>} Resolves to `true` in demo mode; otherwise void.
 * @throws {object} API response if the operation fails.
 */
export const actUndoOrDeleteDocumentation = wrapWithLoading(
	async (documentationId, status = true, demo = false) => {
		if (demo) {
			runInAction(() => {
				rootStore[_storeKey].setIds(
					rootStore[_storeKey].ids.filter(
						(id) => id !== documentationId
					)
				)
				rootStore[_storeKey].setNr(rootStore[_storeKey].nr - 1)
			})

			return true
		}

		const plan_for_deletion = dayjs()
			.add(PLAN_FOR_DELETION_NR_OF_DAYS['documentation'], 'day')
			.unix()
		const timestamp = status ? plan_for_deletion : false

		const _response = await deleteLstItem('documentation', {
			id: documentationId,
			to_delete: timestamp || false
		})
		const _storeItm = requireDocumentationById(documentationId)
		_storeItm.setToDelete(timestamp)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_storeItm.setToDelete(timestamp || false)

				rootStore[_storeKey].setIds(
					rootStore[_storeKey].ids.filter(
						(id) => id !== documentationId
					)
				)
				rootStore[_storeKey].setNr(rootStore[_storeKey].nr - 1)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Del/Undo Documentation' }
)
